import { useMemo, useState } from "react";
import {
  Chip,
  DataTable,
  EmptyState,
  ErrorState,
  LoadingSkeleton,
  Segmented,
} from "@a2z/ui";
import type { DataTableColumn, ChipTone } from "@a2z/ui";
import type {
  AdminAreaSuggestionView,
  AreaSuggestionStatus,
} from "@/lib/api/types";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useAreaSuggestionsQuery } from "../services/useAreaSuggestionsQuery";
import { useAreasForDuplicateHintQuery } from "../services/useAreasForDuplicateHintQuery";
import { looksLikeDuplicate } from "../lib/duplicate-hint";
import { AreaSuggestionActions } from "../components/AreaSuggestionActions";

type StatusFilter = AreaSuggestionStatus | "all";

const STATUS_TONE: Record<AreaSuggestionStatus, ChipTone> = {
  pending: "info",
  approved: "success",
  rejected: "danger",
};

function formatDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale === "ar" ? "ar-EG" : "en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

type Props = {
  locale: string;
};

// Admin queue for buyer-typed "custom area" names that had no catalog match.
// Default filter is "pending", sorted by occurrence count descending (the
// API's default sort) — the most-requested missing area surfaces first.
export function AreaSuggestionsPage({ locale }: Props) {
  const t = useTranslations();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("pending");

  const { data: suggestions, isLoading, error, refetch } =
    useAreaSuggestionsQuery(statusFilter === "all" ? undefined : statusFilter);

  // Scope the duplicate-hint comparison set to only the governorates that
  // actually appear in the current suggestion list — see
  // useAreasForDuplicateHintQuery for why this replaces a full-catalog
  // useAreasQuery() call now that the areas list is paginated.
  const suggestionGovernorateIds = useMemo(
    () => (suggestions ?? []).map((s) => s.governorateId),
    [suggestions],
  );
  const { data: existingAreas } = useAreasForDuplicateHintQuery(
    suggestionGovernorateIds,
  );

  const statusLabel = (status: AreaSuggestionStatus): string =>
    status === "pending"
      ? t(keys.areaSuggestions.statusPending)
      : status === "approved"
        ? t(keys.areaSuggestions.statusApproved)
        : t(keys.areaSuggestions.statusRejected);

  const columns: DataTableColumn<AdminAreaSuggestionView>[] = useMemo(
    () => [
      {
        id: "rawName",
        header: t(keys.areaSuggestions.columnRawName),
        cell: (s) => (
          <div className="min-w-0">
            <span
              dir="auto"
              className="block truncate font-semibold text-[color:var(--text-primary)]"
            >
              {s.rawName}
            </span>
            {existingAreas && looksLikeDuplicate(s, existingAreas) ? (
              <span className="mt-0.5 block text-[13px] text-[color:var(--warning-500)]">
                {t(keys.areaSuggestions.possibleDuplicateHint)}
              </span>
            ) : null}
          </div>
        ),
      },
      {
        id: "governorate",
        header: t(keys.areaSuggestions.columnGovernorate),
        width: "160px",
        cell: (s) =>
          locale === "ar"
            ? s.governorateNameAr
            : (s.governorateNameEn ?? s.governorateNameAr),
      },
      {
        id: "occurrenceCount",
        header: t(keys.areaSuggestions.columnOccurrenceCount),
        width: "110px",
        align: "center",
        cell: (s) => (
          <span className="font-mono text-[13.5px] font-bold text-[color:var(--text-primary)]">
            {s.occurrenceCount}
          </span>
        ),
      },
      {
        id: "lastSeenAt",
        header: t(keys.areaSuggestions.columnLastSeen),
        width: "120px",
        cell: (s) => formatDate(s.lastSeenAt, locale),
      },
      {
        id: "status",
        header: t(keys.areaSuggestions.columnStatus),
        width: "110px",
        cell: (s) => (
          <Chip tone={STATUS_TONE[s.status]} size="sm">
            {statusLabel(s.status)}
          </Chip>
        ),
      },
      {
        id: "actions",
        header: "",
        width: "220px",
        align: "end",
        cell: (s) =>
          s.status === "pending" ? <AreaSuggestionActions suggestion={s} /> : null,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, locale, existingAreas],
  );

  if (isLoading) {
    return <LoadingSkeleton variant="list" rows={6} />;
  }
  if (error || !suggestions) {
    return <ErrorState onRetry={refetch} />;
  }

  const filterOptions = [
    { value: "pending" as StatusFilter, label: t(keys.areaSuggestions.filterPending) },
    { value: "approved" as StatusFilter, label: t(keys.areaSuggestions.filterApproved) },
    { value: "rejected" as StatusFilter, label: t(keys.areaSuggestions.filterRejected) },
    { value: "all" as StatusFilter, label: t(keys.areaSuggestions.filterAll) },
  ];

  return (
    <div className="flex w-full flex-col gap-5 lg:px-6 lg:py-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[24px] font-bold leading-tight tracking-[-0.01em] text-[color:var(--text-primary)]">
            {t(keys.areaSuggestions.title)}
          </h1>
          <p className="mt-1 text-[13.5px] text-[color:var(--text-muted)]">
            {t(keys.areaSuggestions.subtitle)}
          </p>
        </div>
        <Segmented
          value={statusFilter}
          onChange={setStatusFilter}
          options={filterOptions}
          ariaLabel={t(keys.areaSuggestions.columnStatus)}
        />
      </div>

      <DataTable
        rows={suggestions}
        columns={columns}
        rowKey={(s) => s.id}
        ariaLabel={t(keys.areaSuggestions.title)}
        empty={
          <EmptyState
            title={t(keys.common.emptyTitle)}
            description={t(keys.areaSuggestions.noResults)}
          />
        }
      />
    </div>
  );
}
